import { useEffect, useMemo, useState } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import Menu from "@mui/material/Menu";
import MenuItem from "@mui/material/MenuItem";
import Divider from "@mui/material/Divider";
import { useAppState } from "../../hooks/useAppState";
import { getVanillaEl } from "../../domBridge";
import { appState } from "../../../state";
import { notifyStateChange } from "../../../stateBus";
import { mediaUrl } from "../../../mediaAuth";
import { loadAlbums, renameAlbumApi, deleteAlbumAndRefreshGallery } from "../../../albumsApi";
import { loadGalleryData } from "../../../galleryApi";
import { getUploadBridge } from "../../../uploadBridge";
import { confirmDialog, promptDialog } from "../../../dialogs";
import { toggleSelectMode } from "./SelectModeBar";

// Replaces the old static .gallery-header/.gallery-search markup (index.html)
// and the imperative getVanillaEl wiring that used to live in
// GalleryScreen.tsx/GalleryRouteScreen.tsx (favorites filter, view mode,
// refresh, search, title/subtitle/back button) with one real React
// component, portaled into #albumHeroAnchor. Same data GalleryGrid.tsx
// already reads (appState.media/favoritesSet/favoritesOnly/searchQuery),
// just rendered here instead of imperatively pushed into static DOM nodes.
//
// Album Detail (a specific album open) gets the full premium treatment -
// cinematic cover hero, quick actions (Play/Add/Edit), rename/delete in
// the overflow menu. "All Media" (no album open) stays a plain compact
// header + toolbar - there's no single "cover" identity for the whole
// collection the way there is for one album, and it's a much larger,
// general-purpose browsing view rather than "a private collection."
//
// SortToolbar.tsx and ReelButton.tsx are unchanged, separate components -
// their own #sortToolbarAnchor/#reelBtnAnchor anchor divs just live inside
// this component's own JSX now instead of in static index.html, which
// getVanillaEl doesn't care about either way.
export function AlbumHeroHeader() {
  const navigate = useNavigate();
  const albumsDetailId = useAppState((s) => s.albumsDetailId);
  const albums = useAppState((s) => s.albums);
  const media = useAppState((s) => s.media);
  const favoritesSet = useAppState((s) => s.favoritesSet);
  const favoritesOnly = useAppState((s) => s.favoritesOnly);
  const viewMode = useAppState((s) => s.viewMode);
  const searchQuery = useAppState((s) => s.searchQuery);
  const galleryLoading = useAppState((s) => s.galleryLoading);

  const [searchDraft, setSearchDraft] = useState(searchQuery);
  const [menuAnchor, setMenuAnchor] = useState<HTMLElement | null>(null);
  const [coverFailed, setCoverFailed] = useState(false);

  const isAlbumDetail = !!albumsDetailId;
  const album = albumsDetailId ? albums.find((a) => a.id === albumsDetailId) || null : null;

  // Mirrors the old vanilla #gallerySearchInput's own 150ms debounce -
  // local draft state so typing itself is never throttled, only the
  // appState write (and the filtering it triggers) is.
  useEffect(() => {
    const t = setTimeout(() => {
      if (searchDraft !== appState.searchQuery) {
        appState.searchQuery = searchDraft;
        notifyStateChange();
      }
    }, 150);
    return () => clearTimeout(t);
  }, [searchDraft]);

  // A fresh album (or leaving to All Media) starts search over, matching
  // the old behavior of GalleryRouteScreen.tsx never carrying a search
  // term across a navigation.
  useEffect(() => {
    setSearchDraft("");
  }, [albumsDetailId]);

  useEffect(() => setCoverFailed(false), [album?.cover]);

  const visibleCount = useMemo(() => {
    const q = searchQuery.trim().toLowerCase();
    return media.filter((m) => {
      if (favoritesOnly && !favoritesSet.has(m.filename)) return false;
      if (!q) return true;
      return (m.filename || "").toLowerCase().includes(q);
    }).length;
  }, [media, searchQuery, favoritesOnly, favoritesSet]);

  const title = isAlbumDetail ? (album?.name ?? "Album") : "All Media";
  const countLabel = (n: number) => `${n} ${n === 1 ? "memory" : "memories"}`;
  const subtitle = !media.length ? "0 memories" : visibleCount === media.length ? countLabel(media.length) : `${visibleCount} of ${media.length}`;

  function toggleFavoritesOnly(): void {
    appState.favoritesOnly = !appState.favoritesOnly;
    notifyStateChange();
  }

  function setViewMode(mode: "grid" | "list"): void {
    appState.viewMode = mode;
    notifyStateChange();
  }

  function handleRefresh(): void {
    setMenuAnchor(null);
    appState.galleryLoading = true;
    notifyStateChange();
    Promise.all([loadAlbums(), loadGalleryData()]).finally(() => {
      appState.galleryLoading = false;
      notifyStateChange();
    });
  }

  function handleSelectMedia(): void {
    setMenuAnchor(null);
    toggleSelectMode(true);
  }

  async function handleEditAlbum(): Promise<void> {
    setMenuAnchor(null);
    if (!album) return;
    const newName = await promptDialog({
      title: "Rename Album",
      initialValue: album.name,
      placeholder: "Album name",
      confirmLabel: "Rename",
    });
    if (newName && newName !== album.name) renameAlbumApi(album.id, newName);
  }

  async function handleDeleteAlbum(): Promise<void> {
    setMenuAnchor(null);
    if (!album) return;
    const ok = await confirmDialog({
      title: "Delete Album",
      message: `Delete album "${album.name}"? Media will remain in "All Media".`,
      confirmLabel: "Delete",
    });
    if (ok) {
      await deleteAlbumAndRefreshGallery(album.id);
      navigate("/albums");
    }
  }

  function handleAdd(): void {
    getUploadBridge()?.openFilePickerForAlbum(albumsDetailId || "all");
  }

  const anchor = getVanillaEl<HTMLDivElement>("albumHeroAnchor");
  if (!anchor) return null;

  // Album metadata (name/cover/count) hasn't arrived yet for a fresh
  // direct nav/refresh straight to /albums/:id - a real, not fabricated,
  // "still loading" signal (appState.albums simply doesn't have this id
  // yet), distinct from galleryLoading (which tracks the MEDIA fetch).
  const heroPending = isAlbumDetail && !album;

  return createPortal(
    <>
      <div className="album-header-bar">
        {isAlbumDetail && (
          <button type="button" className="album-header-icon-btn" aria-label="Back to Albums" onClick={() => navigate("/albums")}>
            <i className="bi bi-arrow-left" aria-hidden="true" />
          </button>
        )}
        <div className="album-header-titles">
          <h1 className="album-header-title">{title}</h1>
          <span className="album-header-subtitle">{subtitle}</span>
        </div>
        <button
          type="button"
          className={`album-header-icon-btn${favoritesOnly ? " is-active" : ""}`}
          aria-label="Show favorites only"
          aria-pressed={favoritesOnly}
          onClick={toggleFavoritesOnly}
        >
          <i className={`bi ${favoritesOnly ? "bi-heart-fill" : "bi-heart"}`} aria-hidden="true" />
        </button>
        <button
          type="button"
          className="album-header-icon-btn"
          aria-label="More options"
          aria-haspopup="true"
          aria-expanded={!!menuAnchor}
          onClick={(e) => setMenuAnchor(e.currentTarget)}
        >
          <i className="bi bi-three-dots-vertical" aria-hidden="true" />
        </button>
      </div>

      <Menu anchorEl={menuAnchor} open={!!menuAnchor} onClose={() => setMenuAnchor(null)}>
        {isAlbumDetail && album && (
          <MenuItem onClick={handleEditAlbum}>
            <i className="bi bi-pencil" aria-hidden="true" style={{ marginRight: 10 }} />
            Edit album
          </MenuItem>
        )}
        <MenuItem onClick={handleSelectMedia}>
          <i className="bi bi-check2-square" aria-hidden="true" style={{ marginRight: 10 }} />
          Select media
        </MenuItem>
        <MenuItem onClick={handleRefresh}>
          <i className="bi bi-arrow-clockwise" aria-hidden="true" style={{ marginRight: 10 }} />
          Refresh
        </MenuItem>
        {isAlbumDetail &&
          album && [
            <Divider key="divider" />,
            <MenuItem key="delete" onClick={handleDeleteAlbum} sx={{ color: "error.main" }}>
              <i className="bi bi-trash3" aria-hidden="true" style={{ marginRight: 10 }} />
              Delete album
            </MenuItem>,
          ]}
      </Menu>

      {isAlbumDetail &&
        (heroPending ? (
          <div className="album-hero album-hero-skeleton skeleton-card" />
        ) : album ? (
          <>
            <div className="album-hero">
              {album.cover && !coverFailed ? (
                <img
                  src={mediaUrl(`/assets/thumbs/${album.cover}`)}
                  alt=""
                  className="album-hero-cover"
                  onError={() => setCoverFailed(true)}
                />
              ) : (
                <div className="album-hero-cover album-hero-cover-empty">
                  <i className="bi bi-images" aria-hidden="true" />
                </div>
              )}
              <div className="album-hero-scrim" aria-hidden="true" />
              <div className="album-hero-info">
                <div className="album-hero-name">{album.name}</div>
                <div className="album-hero-meta">
                  {subtitle}
                  {album.createdAt &&
                    ` · Created ${new Date(album.createdAt).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" })}`}
                </div>
              </div>
            </div>

            <div className="album-quick-actions">
              {/* ReelButton.tsx portals its own "Play" pill in here when a
                  Memories Reel makes sense (currentAlbum !== "all", already
                  true in this branch) - unchanged component, just restyled
                  to match this row (see ReelButton.tsx). */}
              <div id="reelBtnAnchor" />
              <button type="button" className="album-quick-action" onClick={handleAdd}>
                <span className="album-quick-action-icon">
                  <i className="bi bi-plus-lg" aria-hidden="true" />
                </span>
                <span>Add</span>
              </button>
              <button type="button" className="album-quick-action" onClick={handleEditAlbum}>
                <span className="album-quick-action-icon">
                  <i className="bi bi-pencil" aria-hidden="true" />
                </span>
                <span>Edit</span>
              </button>
            </div>
          </>
        ) : null)}

      <div className="album-toolbar">
        <div className="album-view-toggle" role="group" aria-label="View mode">
          <button type="button" className={viewMode === "grid" ? "is-active" : ""} aria-label="Grid view" onClick={() => setViewMode("grid")}>
            <i className="bi bi-grid-3x3-gap-fill" aria-hidden="true" />
          </button>
          <button type="button" className={viewMode === "list" ? "is-active" : ""} aria-label="List view" onClick={() => setViewMode("list")}>
            <i className="bi bi-list-ul" aria-hidden="true" />
          </button>
        </div>
        {/* Sort pill + dropdown menu (src/react/features/gallery/SortToolbar.tsx),
            unchanged - portals its own content into this anchor. */}
        <div id="sortToolbarAnchor" className="album-toolbar-sort" />
      </div>

      <div className={`album-search${galleryLoading ? " is-loading" : ""}`}>
        <i className="bi bi-search" aria-hidden="true" />
        <input
          type="search"
          placeholder={isAlbumDetail ? "Search this album..." : "Search media..."}
          aria-label="Search media"
          autoComplete="off"
          value={searchDraft}
          onChange={(e) => setSearchDraft(e.target.value)}
        />
      </div>
    </>,
    anchor,
  );
}
